import { useState, useRef } from 'react';
import Dialog from '@raydiant/raydial/components/Dialog';
import Button from '@raydiant/raydial/components/Button';
import Popover, { PopoverProps } from '@raydiant/raydial/components/Popover';
import {
  CircleArrowRight,
  CircleNodes,
  Edit,
  Ellipsis,
  Trash,
} from '@raydiant/raydial/components/Icons';
import {
  DuplicateAndMove,
  ListOl,
  RectangleHistoryCirclePlus,
} from '../../../../../components/Icons';
import ContentCardActionButton from './ContentCardActionButton';
import { Content } from '@raydiant/api-client-js';
import { useSelector } from 'react-redux';
import { selectUserProfile } from '../../../../../selectors/user';
import analytics from '../../../../../clients/analyticsClient';
import {
  canDeleteContent,
  canEditContent,
  canShareContent,
  hasRoleOrHigher,
} from '../../../../../utilities';
import { DEFAULT_QUERY } from '../../../../AddContentPage/AddContentPageV2';
import { LibraryPageModalType } from '../../LibraryPageV2';
import DuplicateMultiple from 'raydiant-elements/icons/Duplicate';

interface ContentCardActionButtonGroupProps {
  content: Content;
  placement?: PopoverProps['placement'];
  libraryQuery?: typeof DEFAULT_QUERY;
  onButtonGroupClick: (contentId: string) => void;
  onButtonGroupItemClick: (type: LibraryPageModalType) => void;
  onButtonGroupClose: () => void;
  onCreateNewPlaylist?: (content: Content) => void;
  onAddToPlaylist?: (content: Content) => void;
  // Duplicate is delegated to the parent so the page-level handler can drive a
  // single progress/success/error toast across all duplicate flows.
  onDuplicateFolder?: (content: Content) => void;
  onDuplicateContent?: (content: Content) => void;
  hideMoveOption?: boolean;
  isTopLevelSharedLibraryFolder?: boolean;
  hideShare?: boolean;
  // Toggles the "Duplicate and Move" menu item. Parent decides based on route
  // + entitlements (My Library only, enterprise + Shared Libraries enabled).
  showDuplicateAndMove?: boolean;
}

const ContentCardActionButtonGroup = ({
  content,
  placement = 'bottom start',
  libraryQuery = DEFAULT_QUERY,
  onButtonGroupClick,
  onButtonGroupItemClick,
  onButtonGroupClose,
  onCreateNewPlaylist,
  onAddToPlaylist,
  hideMoveOption = false,
  isTopLevelSharedLibraryFolder = false,
  hideShare = false,
  onDuplicateFolder,
  onDuplicateContent,
  showDuplicateAndMove = false,
}: ContentCardActionButtonGroupProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);

  const currentUser = useSelector(selectUserProfile);

  const isFolder = content.resourceType === 'folder';
  const canShare = currentUser && canShareContent(currentUser, content);
  const canEdit = currentUser && canEditContent(currentUser, content);
  const canDelete = currentUser && canDeleteContent(currentUser, content);
  const isOwnerOrSuperAdmin =
    currentUser &&
    (content.ownedBy.id === currentUser.id ||
      hasRoleOrHigher('superadmin', currentUser));
  const showRename = !isTopLevelSharedLibraryFolder || !!canEdit;
  const showDelete = !isTopLevelSharedLibraryFolder || !!isOwnerOrSuperAdmin;
  const isMyLibrary =
    libraryQuery.scope === 'owned' && !libraryQuery.profile_id;
  const isSharedLibrary = libraryQuery.scope === 'shared_library';
  // Move is only valid in My Library and Shared Library — never in Users Library or Shared With Me
  const isMoveAllowedScope = isMyLibrary || isSharedLibrary;

  const handleIconClick = () => {
    setIsOpen(true);
    onButtonGroupClick(content.resourceId);
  };

  const trackAction = (
    action: Parameters<
      typeof analytics.trackLibraryCardActionClicked
    >[0]['action'],
  ) => {
    analytics.trackLibraryCardActionClicked({
      action,
      resourceType: content.resourceType,
      resourceId: content.resourceId,
      isFolder,
      libraryScope: libraryQuery.scope,
    });
  };

  // 'folder' is reserved for the create-folder dialog and never reached from this menu.
  const handleItemClick = (
    modalType: Exclude<LibraryPageModalType, 'folder'>,
  ) => {
    setIsOpen(false);
    trackAction(modalType);
    onButtonGroupItemClick(modalType);
  };

  if (!currentUser) return null;
  return (
    <>
      <Button
        variant="unstyled"
        className="bg-white w-6 h-6 rounded-xl flex items-center justify-center pl-[0.5px]"
        onPress={() => handleIconClick()}
      >
        <span ref={triggerRef} className="rotate-90 mb-0.5">
          <Ellipsis />
        </span>
      </Button>
      <Popover
        triggerRef={triggerRef}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        className="w-[230px]"
        placement={placement}
      >
        <Dialog className="px-0 py-2" aria-label="manage content">
          <div className="overflow-hidden">
            {!hideShare && (
              <ContentCardActionButton
                icon={<CircleNodes size={17} />}
                label="Share"
                onClick={() => handleItemClick('share')}
                isDisabled={!canShare}
              />
            )}

            {showRename && (
              <ContentCardActionButton
                icon={<Edit size={17} />}
                label="Rename"
                onClick={() => handleItemClick('rename')}
                isDisabled={!canEdit}
              />
            )}

            {((isFolder && !!onDuplicateFolder) ||
              (!isFolder && !!onDuplicateContent)) && (
              <ContentCardActionButton
                icon={<DuplicateMultiple className="size-[17px]" />}
                label="Duplicate"
                onClick={() => {
                  setIsOpen(false);
                  trackAction('duplicate');
                  if (isFolder) {
                    onDuplicateFolder?.(content);
                  } else {
                    onDuplicateContent?.(content);
                  }
                }}
              />
            )}

            {showDuplicateAndMove && (
              <ContentCardActionButton
                icon={<DuplicateAndMove size={17} />}
                label="Copy to Shared Library"
                onClick={() => handleItemClick('duplicate_and_move')}
              />
            )}

            {!isFolder && onCreateNewPlaylist && (
              <ContentCardActionButton
                icon={<ListOl size={17} />}
                label="Create new playlist"
                onClick={() => {
                  setIsOpen(false);
                  trackAction('create_new_playlist');
                  onCreateNewPlaylist(content);
                }}
              />
            )}

            {!isFolder && onAddToPlaylist && (
              <ContentCardActionButton
                icon={<RectangleHistoryCirclePlus size={17} />}
                label="Add to playlist"
                onClick={() => {
                  setIsOpen(false);
                  trackAction('add_to_playlist');
                  onAddToPlaylist(content);
                }}
              />
            )}

            {!hideMoveOption && (
              <ContentCardActionButton
                icon={<CircleArrowRight size={17} />}
                label="Move"
                onClick={() => handleItemClick('move')}
                isDisabled={!canDelete || !isMoveAllowedScope}
              />
            )}

            {showDelete && (
              <ContentCardActionButton
                className="text-red-700"
                icon={<Trash size={17} />}
                label="Delete"
                onClick={() => handleItemClick('delete')}
                isDisabled={!canDelete}
              />
            )}
          </div>
        </Dialog>
      </Popover>
    </>
  );
};

export default ContentCardActionButtonGroup;
